import React, { useMemo, useState } from "react";
import { searchEventsWithAI } from "../services/apiAI.js";

function SmartEventsGen() {
    const [country, setCountry] = useState("Australia");
    const [stateRegion, setStateRegion] = useState("");
    const [prompt, setPrompt] = useState(`Return upcoming queer party events as JSON matching this schema:

[
  {
    "Name": "Glitter & Beads",
    "StartDateTime": "2025-09-14T21:00:00",
    "Country": "Australia",
    "State": "VIC",
    "City": "Melbourne",
    "Venue": "132 Collins St",
    "EventUrl": "https://example.com/event",
    "EventTicketsUrl": "https://example.com/tickets",
    "Type": "Club Night"
  }
]

Rules:
- City must be present.
- Use ISO 8601 for times (local to the city).
- Include EventUrl if available, and EventTicketsUrl if different.
- Keep 3–10 high-confidence events only.

Target:
Country: Australia
State: VIC
City: Melbourne
Timeframe: next 30 days`);
    const [loading, setLoading] = useState(false);
    const [responseText, setResponseText] = useState("");

    const AU_STATES = useMemo(
        () => [
            { code: "ACT", name: "Australian Capital Territory" },
            { code: "NSW", name: "New South Wales" },
            { code: "NT", name: "Northern Territory" },
            { code: "QLD", name: "Queensland" },
            { code: "SA", name: "South Australia" },
            { code: "TAS", name: "Tasmania" },
            { code: "VIC", name: "Victoria" },
            { code: "WA", name: "Western Australia" },
        ],
        []
    );

    const COUNTRIES = useMemo(
        () => [
            { code: "Australia", name: "Australia" },
            { code: "International", name: "International (Travel)" },
        ],
        []
    );

    const handleFetch = async () => {
        try {
            setLoading(true);
            setResponseText("");

            // Call your API wrapper
            const result = await searchEventsWithAI({
                country,
                state: stateRegion,
                prompt,
            });

            setResponseText(JSON.stringify(result, null, 2));
        } catch (err) {
            setResponseText(`Error: ${err?.message || String(err)}`);
        } finally {
            setLoading(false);
        }
    };

    const isAustralia = country === "Australia";

    return (
        <div className="w-full max-w-3xl mx-auto space-y-6 p-4">
            <h1 className="text-2xl font-bold">Smart Events Generator</h1>

            {/* Country & State */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium">Country</span>
                    <select
                        value={country}
                        onChange={(e) => {
                            const value = e.target.value;
                            setCountry(value);
                            if (value !== "Australia") setStateRegion("");
                        }}
                        className="border rounded-md px-3 py-2 bg-black/5 dark:bg-white/5"
                    >
                        {COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="flex flex-col gap-2">
                    <span className="text-sm font-medium">State / Territory</span>
                    <select
                        value={stateRegion}
                        onChange={(e) => setStateRegion(e.target.value)}
                        disabled={!isAustralia}
                        className="border rounded-md px-3 py-2 bg-black/5 dark:bg-white/5 disabled:opacity-50"
                    >
                        <option value="">(Select…)</option>
                        {AU_STATES.map((s) => (
                            <option key={s.code} value={s.code}>
                                {s.code} — {s.name}
                            </option>
                        ))}
                    </select>
                    {!isAustralia && (
                        <span className="text-xs text-gray-500">
              State is only required for Australia.
            </span>
                    )}
                </label>
            </div>

            {/* Prompt textbox */}
            <label className="flex flex-col gap-2">
                <span className="text-sm font-medium">Prompt / Instructions</span>
                <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={12}
                    className="border rounded-md px-3 py-2 font-mono text-sm bg-black/5 dark:bg-white/5"
                    placeholder="Describe what to fetch…"
                />
            </label>

            {/* Fetch button */}
            <div className="flex items-center gap-3">
                <button
                    onClick={handleFetch}
                    disabled={loading}
                    className="px-4 py-2 rounded-lg font-semibold border hover:shadow disabled:opacity-50"
                >
                    {loading ? "Fetching…" : "Fetch Events"}
                </button>
                <span className="text-sm text-gray-500">
          Uses GPT API defined in <code>apiAI.js</code>
        </span>
            </div>

            {/* Response textbox */}
            <label className="flex flex-col gap-2">
                <span className="text-sm font-medium">Response</span>
                <textarea
                    readOnly
                    value={responseText}
                    rows={12}
                    className="border rounded-md px-3 py-2 font-mono text-sm bg-black/5 dark:bg-white/5"
                    placeholder="API response will appear here…"
                />
            </label>
        </div>
    );
}

export default SmartEventsGen;
